/* ============================================================
   HOOK: useImageUpload
   - uploadImage(file) : téléverse vers ImgBB et retourne l'URL
   - createPreview(file) : crée un aperçu local instantané
   ============================================================ */

import { useState } from 'react';

const IMGBB_API_KEY = import.meta.env.VITE_IMGBB_API_KEY;

interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

interface UseImageUploadReturn {
  /** Téléverse un fichier vers ImgBB et retourne l'URL distante */
  uploadImage: (file: File) => Promise<UploadResult>;
  /** Crée un aperçu local instantané (pas de réseau) */
  createPreview: (file: File) => string;
  /** Libère un aperçu local */
  revokePreview: (url: string) => void;
  isUploading: boolean;
  error: string | null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Upload direct browser → ImgBB */
async function directUpload(file: File): Promise<UploadResult> {
  if (!IMGBB_API_KEY) {
    throw new Error('Clé ImgBB non configurée (VITE_IMGBB_API_KEY)');
  }

  const base64 = await fileToBase64(file);

  const formData = new FormData();
  formData.append('key', IMGBB_API_KEY);
  formData.append('image', base64);
  formData.append('name', file.name.replace(/\.[^.]+$/, ''));

  const response = await fetch('https://api.imgbb.com/1/upload', {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`ImgBB HTTP ${response.status}`);
  }

  const data = await response.json();
  if (data.success && data.data?.url) {
    return { success: true, url: data.data.url };
  }
  throw new Error(data.error?.message || "Échec de l'upload ImgBB");
}

// L'upload est remplacé par une conversion locale en Base64
// Cela permet de sauvegarder l'URL d'image base64 directement dans la BD, de façon instantanée.

const VALID_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

function validateFile(file: File): string | null {
  if (!VALID_TYPES.includes(file.type)) {
    return 'Type de fichier non autorisé. Formats acceptés: JPG, PNG, GIF, WEBP';
  }
  if (file.size > MAX_SIZE) {
    return 'Fichier trop volumineux (max 5 MB)';
  }
  return null;
}

export function useImageUpload(): UseImageUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Crée un aperçu local instantané — aucun appel réseau */
  const createPreview = (file: File): string => {
    return URL.createObjectURL(file);
  };

  /** Libère la mémoire d'un aperçu local */
  const revokePreview = (url: string) => {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  /** Téléverse vers ImgBB (direct browser puis fallback serveur) */
  const uploadImage = async (file: File): Promise<UploadResult> => {
    setIsUploading(true);
    setError(null);

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setIsUploading(false);
      return { success: false, error: validationError };
    }

    try {
      // Upload via ImgBB (renvoie l'URL distante, pas de Base64 massif en BDD)
      const result = await directUpload(file);
      setIsUploading(false);
      return result;
    } catch (err: any) {
      const errorMsg = err?.message || "Erreur lors de l'upload de l'image";
      console.error('[useImageUpload]', err);
      setError(errorMsg);
      setIsUploading(false);
      return { success: false, error: errorMsg };
    }
  };

  return {
    uploadImage,
    createPreview,
    revokePreview,
    isUploading,
    error,
  };
}
